import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import { useAiMessageEvents } from '../../../../api/denaliAssistant'
import Loading from '../../../../components/molecules/Loading'
import NoResults from '../common/NoResults'
import SummitOnlyContent from '../../../../components/atoms/SummitOnlyContent'
import Divider from '../../../../components/atoms/Divider'
import ExplainabilityToolbar from './ExplainabilityToolbar'
import { ThoughtBlock, ToolCallVisualizer, StepChangeIndicator } from './index'

const useStyles = makeStyles((theme) => ({
  explainabilitySection: {
    // Events rendered inline with timeline
    marginBottom: 'var(--margin-xl)',
    '& > *:first-child': {
      marginTop: 0 // Remove top margin from first event
    }
  }
}))

function ExplainabilitySection ({ parentInfo, messageId, visible = true, isGenerating = false }) {
  const classes = useStyles()
  const wasGeneratingRef = useRef(isGenerating)

  // Poll for events while generating, stop when complete
  const { data, isLoading, refetch } = useAiMessageEvents(parentInfo, messageId, {
    enabled: !!visible,
    refetchInterval: (isGenerating && visible) ? 2500 : false
  })

  // Perform final fetch when generation completes
  useEffect(() => {
    if (wasGeneratingRef.current && !isGenerating && visible) {
      // Generation just completed, do a final fetch
      refetch()
    }
    wasGeneratingRef.current = isGenerating
  }, [isGenerating, visible, refetch])

  const events = data?.events || []

  if (!visible) {
    return null
  }

  if (isLoading) {
    return (
      <Loading />
    )
  }

  if (!events?.length) {
    return (
      <NoResults className='opal-card' message='No Explainability Events Found' />
    )
  }

  const renderEvent = (event) => {
    switch (event.type) {
      case 'thought':
        return <ThoughtBlock key={event.eventId} {...event} />
      case 'toolCall':
        return <ToolCallVisualizer key={event.eventId} {...event} messageId={messageId} parentInfo={parentInfo} />
      case 'stepChange':
        return <StepChangeIndicator key={event.eventId} {...event} />
      default:
        return null
    }
  }

  return (
    <FadeIn className={classes.explainabilitySection}>
      <FlexColumn
        className='gap-xs radius-md px-4 pt-1 pb-5'
        style={{
          border: 'var(--dai-border1)',
          backgroundColor: 'var(--dai-color-surface4)'
        }}
      >
        {events.map(renderEvent)}
        <SummitOnlyContent>
          <Divider />
          <ExplainabilityToolbar events={data?.events} />
        </SummitOnlyContent>
      </FlexColumn>
    </FadeIn>
  )
}

ExplainabilitySection.propTypes = {
  parentInfo: PropTypes.shape({
    parentId: PropTypes.string,
    parentType: PropTypes.oneOf(['schedule', 'thread'])
  }),
  messageId: PropTypes.string,
  visible: PropTypes.bool,
  isGenerating: PropTypes.oneOfType([PropTypes.bool, PropTypes.number])
}

export default ExplainabilitySection
